<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useForm } from 'vee-validate';
import { useMutation } from '@tanstack/vue-query';
import * as yup from 'yup';
import { passwordsApi, type PasswordScope } from '@/api/passwords';
import { takeFragmentToken } from '@/utils/fragment-token';
import type { AxiosError } from 'axios';

const props = withDefaults(defineProps<{ scope?: PasswordScope }>(), { scope: 'caregiver' });

const { t } = useI18n();

const MIN_PASSWORD_LENGTH = 8;

const validationSchema = yup.object({
  password: yup.string()
    .min(MIN_PASSWORD_LENGTH, t('editPassword.passwordMinLength'))
    .required(t('editPassword.passwordRequired')),
  passwordConfirmation: yup.string()
    .min(MIN_PASSWORD_LENGTH, t('editPassword.passwordMinLength'))
    .required(t('editPassword.passwordConfirmationRequired'))
    .oneOf([yup.ref('password')], t('editPassword.passwordConfirmationMismatch')),
});

const { handleSubmit, values, errors, defineField } = useForm({ validationSchema });

const [password, passwordAttrs] = defineField('password');
const [passwordConfirmation, passwordConfirmationAttrs] = defineField('passwordConfirmation');

// The emailed link carries the token in the URL fragment, never sent to the server.
const recoveryToken = takeFragmentToken('reset_password_token');

const { mutate: editPassword, isError, error, isPending, isSuccess } = useMutation<unknown, AxiosError>(
  {
    mutationFn: () => passwordsApi(props.scope).update({
      ...values,
      resetPasswordToken: recoveryToken || '',
    }),
  },
);

const onSubmit = handleSubmit(() => editPassword());

// A 422 lists the rejected attributes, e.g. { errors: ['reset_password_token'] }.
const ERROR_MESSAGE_KEYS: Record<string, string> = {
  'reset_password_token': 'editPassword.tokenInvalid',
  'password': 'editPassword.passwordInvalid',
  'password_confirmation': 'editPassword.passwordConfirmationMismatch',
};

const rejectedAttributes = computed(() => (
  (error.value?.response?.data as { errors?: string[] } | undefined)?.errors ?? []
));

const tokenInvalid = computed(() => isError.value && rejectedAttributes.value.includes('reset_password_token'));

const newLinkPath = computed(() => `/${props.scope}s/password/new`);

const errorText = computed(() => {
  const messageKey = rejectedAttributes.value.map(attribute => ERROR_MESSAGE_KEYS[attribute]).find(Boolean);

  return isError.value && messageKey ? t(messageKey) : '';
});
</script>
<template>
  <div class="w-auto">
    <div
      v-if="isSuccess"
      class="justify-center d-flex align-center"
      style="min-height: 60vh;"
    >
      <v-card
        class="text-center pa-8"
        max-width="500"
      >
        <v-icon
          color="success"
          size="80"
          class="mb-4"
        >
          mdi-check-circle
        </v-icon>
        <h2 class="mb-4 text-h4 font-weight-bold text-success">
          {{ t('passwordUpdateSuccess.title') }}
        </h2>
        <p class="mb-6 text-body-1 text-grey-darken-1">
          {{ t('passwordUpdateSuccess.message') }}
        </p>
        <p class="text-body-2 text-grey">
          {{ t('passwordUpdateSuccess.closeTab') }}
        </p>
      </v-card>
    </div>
    <div v-else>
      <h3 class="mb-2 text-2xl font-semibold">
        {{ t('editPassword.title') }}
      </h3>
      <p class="mb-8 text-gray-400">
        {{ t('editPassword.subtitle') }}
      </p>
      <v-form @submit.prevent="onSubmit">
        <v-text-field
          v-model="password"
          v-bind="passwordAttrs"
          type="password"
          variant="outlined"
          :label="t('editPassword.password')"
          :error-messages="errors.password"
          :disabled="isPending"
        />
        <v-text-field
          v-model="passwordConfirmation"
          v-bind="passwordConfirmationAttrs"
          type="password"
          variant="outlined"
          :label="t('editPassword.passwordConfirmation')"
          :error-messages="errors.passwordConfirmation"
          :disabled="isPending"
        />
        <v-btn
          color="primary"
          type="submit"
          :loading="isPending"
          :disabled="isPending"
        >
          {{ t('editPassword.submit') }}
        </v-btn>
      </v-form>
      <v-alert
        v-if="isError"
        class="mt-4 rounded"
        :type="'error'"
      >
        {{ errorText || t('userSession.defaultError') }}
        <a
          v-if="tokenInvalid"
          :href="newLinkPath"
          class="d-block mt-2 text-white"
        >
          {{ t('editPassword.requestNewLink') }}
        </a>
      </v-alert>
    </div>
  </div>
</template>
